import { z } from 'zod';
import type { IsoDate } from './common.js';

export const displayNameSchema = z.string().trim().min(1, 'Obligatorio').max(60);

/** Languages the app speaks (interface, API errors and emails). Spanish is the default. */
export const locales = ['es', 'en'] as const;
export const localeSchema = z.enum(locales);
export type Locale = z.infer<typeof localeSchema>;

/** IANA time zone (e.g. «Europe/Madrid»), as the device reports it. */
export const timeZoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine((zone) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: zone });
      return true;
    } catch {
      return false;
    }
  }, 'Zona horaria no válida');

export const updateProfileSchema = z.object({
  displayName: displayNameSchema.optional(),
  title: z.string().trim().max(80).nullable().optional(),
  avatarUrl: z.url().max(500).nullable().optional(),
  locale: localeSchema.optional(),
  timeZone: timeZoneSchema.optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/** Custom status like «🏖️ De vacaciones hasta el lunes». */
export interface UserStatus {
  emoji: string;
  text: string;
  /** Cleared automatically after this moment (null: until changed). */
  expiresAt: IsoDate | null;
}

/** Daily do-not-disturb window in the user's time zone, e.g. 22:00 → 08:00. */
export interface QuietHours {
  start: string;
  end: string;
  timeZone: string;
}

/** Public information about any user. */
export interface UserSummary {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  title: string | null;
  status: UserStatus | null;
  /** Do not disturb right now (manually or within quiet hours). */
  dnd: boolean;
  /** Inactive for a while (idle) although connected. */
  away: boolean;
  /** Part of the Nexo team (platform admin): shown with a special badge. */
  staff?: boolean;
}

/** What the user card shows about someone you share a team with (like Discord's profile). */
export interface UserProfile extends UserSummary {
  /** When they joined Nexo. */
  createdAt: IsoDate;
  /** Teams both of you are in. */
  mutualTeams: { id: string; name: string }[];
}

/** The authenticated user's own profile. */
export interface Me extends UserSummary {
  email: string;
  createdAt: IsoDate;
  /** Platform administrator (access to /admin). */
  isPlatformAdmin: boolean;
  /** Manual do-not-disturb until this moment. */
  dndUntil: IsoDate | null;
  quietHours: QuietHours | null;
  /** Receive the daily summary email. */
  dailyDigest: boolean;
  /** Last version whose news («Novedades») the user has seen. */
  lastSeenVersion: string | null;
  /** Account language: emails and the interface on a device without its own choice. */
  locale: Locale;
  /** Where the user is (from their device): emails such as the daily digest use it. */
  timeZone: string | null;
}

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Formato HH:MM');

export const updateStatusSchema = z.object({
  emoji: z.string().trim().min(1).max(16),
  text: z.string().trim().max(100).default(''),
  expiresAt: z.iso.datetime().nullable().default(null),
});
export type UpdateStatusInput = z.input<typeof updateStatusSchema>;

export const updateDndSchema = z.object({
  /** Null turns manual do-not-disturb off. */
  until: z.iso.datetime().nullable(),
});
export type UpdateDndInput = z.infer<typeof updateDndSchema>;

export const updatePreferencesSchema = z.object({
  quietHours: z
    .object({ start: hhmm, end: hhmm, timeZone: z.string().min(1).max(64) })
    .nullable()
    .optional(),
  dailyDigest: z.boolean().optional(),
});
export type UpdatePreferencesInput = z.infer<typeof updatePreferencesSchema>;

export const presenceSchema = z.object({ away: z.boolean() });
export type PresenceInput = z.infer<typeof presenceSchema>;

/** Minutes since midnight of `date` in `timeZone`. */
function minutesIn(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return get('hour') * 60 + get('minute');
}

const toMinutes = (value: string) => {
  const [h = 0, m = 0] = value.split(':').map(Number);
  return h * 60 + m;
};

/** Whether `now` falls within the quiet hours (windows may cross midnight). */
export function inQuietHours(quiet: QuietHours | null, now = new Date()): boolean {
  if (!quiet) return false;
  let current: number;
  try {
    current = minutesIn(now, quiet.timeZone);
  } catch {
    return false;
  }
  const start = toMinutes(quiet.start);
  const end = toMinutes(quiet.end);
  if (start === end) return false;
  return start < end ? current >= start && current < end : current >= start || current < end;
}

/** Effective do-not-disturb state. */
export function isDnd(
  user: { dndUntil: IsoDate | null; quietHours: QuietHours | null },
  now = new Date(),
): boolean {
  return (
    (user.dndUntil !== null && new Date(user.dndUntil) > now) || inQuietHours(user.quietHours, now)
  );
}

/** Deleting your own account asks for the password again. */
export const deleteAccountSchema = z.object({
  password: z.string().min(1, 'Obligatorio'),
});
export type DeleteAccountInput = z.infer<typeof deleteAccountSchema>;

/** Teams that keep someone from deleting their account (they own them and others are in them). */
export interface DeleteAccountBlocked {
  teams: { id: string; name: string; memberCount: number }[];
}
